<template>
  <Admin style="background: #f2f2f2">
    <v-main>
      <v-container>
        <v-card class="rounded-xl tw-min-h-80" v-if="!isProductListLoading">
          <v-card-text v-if="!selectedCompany.isDemo">
            <v-breadcrumbs :items="items">
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                  :to="item.to"
                  :disabled="item.disabled"
                  :exact="true"
                >
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>

            <h2 class="tw-text-xl tw-font-bold tw-text-black tw-mb-6 tw-px-4">
              สร้างสำเนาบริษัท
            </h2>

            <!-- Company Info -->
            <div
              class="tw-border tw-rounded-xl tw-p-5 tw-mb-6 tw-bg-gray-50 tw-flex tw-items-center tw-gap-4 tw-mx-4"
            >
              <v-avatar size="48" color="#f5f5f5">
                <img
                  v-if="selectedCompany.imageLogoUrl"
                  :src="selectedCompany.imageLogoUrl"
                  :alt="selectedCompany.nameTH"
                  style="object-fit: contain;"
                />
                <span v-else class="tw-text-xl tw-font-bold tw-text-gray-400">
                  {{ (selectedCompany.nameEN || selectedCompany.nameTH || '?')[0].toUpperCase() }}
                </span>
              </v-avatar>
              <div>
                <div class="tw-text-xs tw-text-gray-400">บริษัทที่เลือก</div>
                <div class="tw-font-bold tw-text-black tw-text-base">
                  {{ selectedCompany.nameTH }}
                </div>
              </div>
            </div>

            <!-- Previously created duplicates -->
            <div class="tw-px-4 tw-mb-6">
              <div class="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                <h3 class="tw-text-base tw-font-bold tw-text-black">
                  สำเนาที่เคยสร้างจากบริษัทนี้
                </h3>
                <v-btn
                  x-small
                  icon
                  color="grey"
                  title="รีเฟรช"
                  @click="loadPreviousCopiesCount"
                >
                  <v-icon x-small>mdi-refresh</v-icon>
                </v-btn>
              </div>

              <div class="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-mb-2">
                <template v-if="previousCopies.loading">
                  <v-progress-circular
                    indeterminate
                    size="14"
                    width="2"
                    color="grey"
                  />
                  <span class="tw-text-gray-500">
                    กำลังตรวจสอบประวัติการสร้างสำเนา…
                  </span>
                </template>
                <template v-else-if="previousCopies.error">
                  <v-icon x-small color="orange">mdi-alert</v-icon>
                  <span class="tw-text-orange-600">
                    ตรวจสอบประวัติไม่ได้: {{ previousCopies.error }}
                  </span>
                  <v-btn
                    x-small
                    text
                    color="primary"
                    @click="loadPreviousCopiesCount"
                  >
                    ลองใหม่
                  </v-btn>
                </template>
                <template v-else-if="previousCopies.count > 0">
                  <v-chip
                    small
                    label
                    color="indigo lighten-5"
                    text-color="indigo darken-3"
                  >
                    <v-icon x-small left>mdi-content-copy</v-icon>
                    สร้างสำเนาแล้ว
                    {{ previousCopies.count }}{{ previousCopies.capped ? "+" : "" }}
                    บริษัท
                  </v-chip>
                  <span
                    v-if="previousCopies.lastAtText"
                    class="tw-text-gray-500"
                  >
                    ล่าสุด: {{ previousCopies.lastAtText }}
                  </span>
                </template>
                <template v-else>
                  <v-icon x-small color="grey">mdi-information-outline</v-icon>
                  <span class="tw-text-gray-500">
                    ยังไม่เคยสร้างสำเนาจากบริษัทนี้
                  </span>
                </template>
              </div>

              <div
                v-if="selectedDuplicates.length > 0"
                class="tw-flex tw-items-center tw-gap-2 tw-mb-2"
              >
                <v-chip small color="red lighten-4" text-color="red darken-3">
                  เลือกแล้ว {{ selectedDuplicates.length }} รายการ
                </v-chip>
                <v-btn
                  small
                  color="red darken-1"
                  depressed
                  dark
                  :disabled="deleteState.running"
                  @click="confirmDelete(selectedDuplicates)"
                >
                  <v-icon small left>mdi-delete</v-icon>
                  ลบที่เลือก
                </v-btn>
                <v-btn
                  small
                  text
                  :disabled="deleteState.running"
                  @click="selectedDuplicates = []"
                >
                  ยกเลิกการเลือก
                </v-btn>
              </div>

              <v-data-table
                v-if="previousCopies.count > 0 && !previousCopies.loading"
                v-model="selectedDuplicates"
                dense
                show-select
                item-key="id"
                :headers="previousCopiesHeaders"
                :items="previousCopies.items"
                :items-per-page="10"
                :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
                class="tw-text-xs tw-border tw-rounded-xl"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip
                    x-small
                    label
                    :color="statusChipColor(item.status)"
                    text-color="white"
                  >
                    {{ item.status || "-" }}
                  </v-chip>
                </template>
                <template v-slot:item.basePath="{ item }">
                  <span class="tw-font-mono tw-text-xs">
                    {{ item.basePath || "-" }}
                  </span>
                </template>
                <template v-slot:item.id="{ item }">
                  <div class="tw-flex tw-items-center tw-gap-1">
                    <span class="tw-font-mono tw-text-xs">{{ item.id }}</span>
                    <v-btn
                      x-small
                      icon
                      color="grey"
                      title="คัดลอก ID"
                      @click="copyToClipboard(item.id)"
                    >
                      <v-icon x-small>mdi-content-copy</v-icon>
                    </v-btn>
                    <v-btn
                      x-small
                      icon
                      color="primary"
                      target="_blank"
                      title="เปิดหน้าตั้งค่าบริษัท"
                      :to="{ name: 'Company.Settings', params: { companyId: item.id } }"
                    >
                      <v-icon x-small>mdi-open-in-new</v-icon>
                    </v-btn>
                    <v-btn
                      x-small
                      icon
                      color="red"
                      title="ลบสำเนานี้"
                      :disabled="deleteState.running"
                      @click="confirmDelete([item])"
                    >
                      <v-icon x-small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>

              <div
                v-if="previousCopies.capped"
                class="tw-text-xs tw-text-orange-600 tw-mt-1"
              >
                แสดงเฉพาะ {{ previousCopies.items.length }} รายการล่าสุด
                (มีจำนวนทั้งหมดมากกว่านี้)
              </div>
            </div>

            <!-- Delete confirmation dialog -->
            <v-dialog v-model="deleteDialog.show" max-width="560" persistent>
              <v-card>
                <v-card-title class="tw-text-base tw-font-bold">
                  <v-icon left color="red">mdi-alert</v-icon>
                  ยืนยันการลบสำเนา
                </v-card-title>
                <v-card-text>
                  <div class="tw-text-sm tw-text-gray-700 tw-mb-3">
                    คุณกำลังจะลบ
                    <strong class="tw-text-red-600">
                      {{ deleteDialog.targets.length }}
                    </strong>
                    บริษัทสำเนา และข้อมูลย่อยทั้งหมด
                    <span class="tw-text-red-600">การดำเนินการนี้ไม่สามารถย้อนกลับได้</span>
                  </div>
                  <div
                    class="tw-max-h-48 tw-overflow-y-auto tw-border tw-rounded tw-p-2 tw-text-xs tw-bg-gray-50"
                  >
                    <div
                      v-for="t in deleteDialog.targets"
                      :key="t.id"
                      class="tw-flex tw-justify-between tw-py-0.5"
                    >
                      <span>
                        #{{ t.copyNo }} · {{ t.nameTH || t.nameEN || "(no name)" }}
                      </span>
                      <span class="tw-font-mono tw-text-gray-500">{{ t.id }}</span>
                    </div>
                  </div>

                  <div class="tw-mt-3 tw-text-xs">
                    พิมพ์
                    <code class="tw-px-1 tw-bg-gray-100 tw-rounded">DELETE</code>
                    เพื่อยืนยัน:
                    <v-text-field
                      v-model="deleteDialog.confirmText"
                      dense
                      outlined
                      hide-details
                      class="tw-mt-1"
                      :disabled="deleteState.running"
                    />
                  </div>

                  <v-alert
                    v-if="deleteState.error"
                    type="error"
                    dense
                    outlined
                    class="tw-mt-3 tw-text-xs"
                  >
                    {{ deleteState.error }}
                  </v-alert>

                  <div
                    v-if="deleteState.running"
                    class="tw-mt-3 tw-text-xs tw-text-gray-600"
                  >
                    <v-progress-linear
                      indeterminate
                      color="red"
                      class="tw-mb-1"
                    />
                    กำลังลบ {{ deleteState.done }} / {{ deleteState.total }} …
                  </div>

                  <div
                    v-if="deleteState.results.length > 0 && !deleteState.running"
                    class="tw-mt-3 tw-text-xs"
                  >
                    <strong>ผลลัพธ์:</strong>
                    <div
                      v-for="r in deleteState.results"
                      :key="r.id"
                      :class="r.ok ? 'tw-text-green-700' : 'tw-text-red-600'"
                    >
                      {{ r.ok ? "✓" : "✗" }}
                      <span class="tw-font-mono">{{ r.id }}</span>
                      <span v-if="!r.ok"> — {{ r.code }} ({{ r.error }})</span>
                    </div>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <template v-if="deleteState.finished">
                    <v-btn
                      color="primary"
                      depressed
                      :disabled="deleteState.running"
                      @click="closeDeleteDialog"
                    >
                      <v-icon left small>mdi-check</v-icon>
                      Done
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn
                      text
                      :disabled="deleteState.running"
                      @click="closeDeleteDialog"
                    >
                      ยกเลิก
                    </v-btn>
                    <v-btn
                      color="red"
                      depressed
                      dark
                      :disabled="
                        deleteState.running ||
                        deleteDialog.confirmText !== 'DELETE' ||
                        deleteDialog.targets.length === 0
                      "
                      @click="runDelete"
                    >
                      <v-icon left small>mdi-delete</v-icon>
                      ลบทั้งหมด
                    </v-btn>
                  </template>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Product List -->
            <div class="tw-px-4">
              <h3 class="tw-text-base tw-font-bold tw-text-black tw-mb-3">
                ผลิตภัณฑ์ที่ใช้งาน
              </h3>

              <v-row dense>
                <v-col
                  v-for="product in productItems"
                  :key="product.key"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <div
                    class="tw-border tw-rounded-xl tw-p-4 tw-flex tw-items-center tw-justify-between"
                    :class="product.enabled ? 'tw-bg-green-50 tw-border-green-300' : 'tw-bg-gray-50'"
                  >
                    <div>
                      <div class="tw-font-bold tw-text-black tw-flex tw-items-center tw-gap-2">
                        {{ product.label }}
                        <v-chip
                          v-if="product.badge"
                          x-small
                          :color="product.badgeColor"
                          text-color="white"
                        >
                          {{ product.badge }}
                        </v-chip>
                      </div>
                      <div class="tw-text-xs tw-text-gray-500">{{ product.key }}</div>
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-2">
                      <v-btn
                        v-if="product.canDuplicate"
                        small
                        color="primary"
                        depressed
                        :to="{ name: 'Company.Settings.CreateDuplicateCompanyConfig' }"
                      >
                        <v-icon small class="tw-mr-1">mdi-content-duplicate</v-icon>
                        Duplicate
                      </v-btn>
                      <v-icon :color="product.enabled ? 'green' : 'grey lighten-1'">
                        {{ product.enabled ? 'mdi-check-circle' : 'mdi-close-circle-outline' }}
                      </v-icon>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <v-alert
                v-if="!hasAnyProduct"
                type="info"
                outlined
                class="tw-mt-4"
              >
                บริษัทนี้ยังไม่ได้เปิดใช้งานผลิตภัณฑ์ใด ๆ
              </v-alert>
            </div>
          </v-card-text>

          <div
            v-else
            class="align-center justify-center"
            style="height: 200px; display: flex"
          >
            No Permission
          </div>
        </v-card>

        <!-- Loading skeleton when product list is loading -->
        <v-card v-else class="rounded-xl tw-min-h-80 tw-p-7">
          <v-skeleton-loader type="list-item-two-line" style="width: 400px;" />
          <v-skeleton-loader type="list-item-three-line" style="width: 400px;" />
          <v-skeleton-loader type="list-item-three-line" style="width: 400px;" />
        </v-card>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
import Admin from "../../components/admin/admin-main-template";
import { mapState } from "vuex";
import fc from "@/firebaseConfig";
import axios from "axios";

const PRODUCT_LABELS = [
  { key: "isEtax", label: "E-Tax" },
  { key: "isPdfDoc", label: "PDF Document" },
  { key: "isApAutomate", label: "AP Automation" },
  { key: "isNsThaiSmartTax", label: "NetSuite Thai Smart Tax" },
  { key: "isNsThaiSmartTaxAverage", label: "NetSuite Thai Smart Tax (Average)" },
  { key: "isFixedAssetCount", label: "Fixed Asset Count" },
  { key: "isExchangeRate", label: "Exchange Rate" },
  { key: "isTopserv", label: "Topserv" },
  { key: "isLab", label: "Lab" },
];

export default {
  name: "CreateDuplicateCompany",
  components: { Admin },
  computed: {
    ...mapState({
      selectedCompany: (state) => state.companies.selectedCompany,
      selectedCompanyProductList: (state) =>
        state.companies.selectedCompanyProductList,
      isProductListLoading: (state) => state.companies.isProductListLoading,
    }),

    productItems() {
      const list = this.selectedCompanyProductList || {};
      return PRODUCT_LABELS.map((p) => {
        const enabled = !!list[p.key];
        let badge = "";
        let badgeColor = "";
        let canDuplicate = false;
        if (p.key === "isApAutomate" && enabled) {
          if (list.isApAutomateType === 1) {
            badge = "SAP B1";
            badgeColor = "blue";
          } else if (list.isApAutomateType === 2) {
            badge = "EXPRESS";
            badgeColor = "orange";
            canDuplicate = true;
          }
        }
        return {
          key: p.key,
          label: p.label,
          enabled: enabled,
          badge: badge,
          badgeColor: badgeColor,
          canDuplicate: canDuplicate,
        };
      });
    },

    hasAnyProduct() {
      return this.productItems.some((p) => p.enabled);
    },
  },

  data: () => ({
    items: [
      { text: "ตั้งค่า", disabled: false, to: { name: "Company.Settings" } },
      { text: "สร้างสำเนาบริษัท", disabled: true },
    ],
    previousCopies: {
      count: 0,
      capped: false,
      lastAt: null,
      lastAtText: "",
      loading: false,
      error: "",
      items: [],
    },
    previousCopiesHeaders: [
      { text: "Copy #", value: "copyNo", width: 90 },
      { text: "Name (TH)", value: "nameTH" },
      { text: "Name (EN)", value: "nameEN" },
      { text: "Tax ID", value: "taxID", width: 130 },
      { text: "Base Path", value: "basePath" },
      { text: "Status", value: "status", width: 110 },
      { text: "Created", value: "createdAtText", width: 170 },
      { text: "Actions", value: "id", width: 300, sortable: false },
    ],
    selectedDuplicates: [],
    deleteDialog: {
      show: false,
      targets: [],
      confirmText: "",
    },
    deleteState: {
      running: false,
      finished: false,
      done: 0,
      total: 0,
      results: [],
      error: "",
    },
  }),

  created() {
    const companyId = this.$route.params.companyId;
    if (companyId) {
      this.$store.dispatch("companies/getCompanyProductList", { companyId });
      this.loadPreviousCopiesCount();
    }
  },

  methods: {
    async loadPreviousCopiesCount() {
      const companyId = this.$route.params.companyId;
      if (!companyId) return;
      const PROBE_LIMIT = 500;
      this.previousCopies.loading = true;
      this.previousCopies.error = "";
      try {
        const snap = await fc.companies
          .where("duplicateCompany.sourceCompanyId", "==", companyId)
          .limit(PROBE_LIMIT)
          .get();

        const items = [];
        let lastMs = 0;
        snap.forEach((doc) => {
          const data = doc.data() || {};
          const dc = data.duplicateCompany || {};
          const t = dc.createdAt || dc.updatedAt || null;
          let ms = 0;
          if (t && typeof t.toMillis === "function") ms = t.toMillis();
          else if (t && t.seconds) ms = t.seconds * 1000;
          if (ms > lastMs) lastMs = ms;
          items.push({
            id:            doc.id,
            copyNo:        Number(dc.copyNo) || 0,
            nameTH:        data.nameTH || "",
            nameEN:        data.nameEN || "",
            taxID:         data.taxID  || "",
            basePath:      dc.basePath || "",
            status:        dc.status   || "",
            runId:         dc.runId    || "",
            createdAtMs:   ms,
            createdAtText: ms ? new Date(ms).toLocaleString() : "",
          });
        });
        items.sort((a, b) => b.createdAtMs - a.createdAtMs);

        this.previousCopies.items = items;
        this.previousCopies.count = snap.size;
        this.previousCopies.capped = snap.size >= PROBE_LIMIT;
        this.previousCopies.lastAt = lastMs || null;
        this.previousCopies.lastAtText = lastMs
          ? new Date(lastMs).toLocaleString()
          : "";
      } catch (err) {
        console.error("loadPreviousCopiesCount error:", err);
        this.previousCopies.error =
          (err && err.message) || "query failed";
      } finally {
        this.previousCopies.loading = false;
      }
    },

    statusChipColor(status) {
      if (status === "ready")   return "green";
      if (status === "copying") return "blue";
      if (status === "failed")  return "red";
      return "grey";
    },

    copyToClipboard(text) {
      if (!text) return;
      try {
        navigator.clipboard.writeText(text);
      } catch (e) { /* swallow */ }
    },

    confirmDelete(targets) {
      const list = Array.isArray(targets) ? targets.slice() : [];
      if (list.length === 0) return;
      this.deleteDialog.targets = list;
      this.deleteDialog.confirmText = "";
      this.deleteState.running = false;
      this.deleteState.finished = false;
      this.deleteState.done = 0;
      this.deleteState.total = 0;
      this.deleteState.results = [];
      this.deleteState.error = "";
      this.deleteDialog.show = true;
    },

    closeDeleteDialog() {
      if (this.deleteState.running) return;
      this.deleteDialog.show = false;
    },

    async runDelete() {
      const sourceCompanyId = this.$route.params.companyId;
      const targets = (this.deleteDialog.targets || []).slice();
      if (!sourceCompanyId || targets.length === 0) return;

      this.deleteState.running = true;
      this.deleteState.error = "";
      this.deleteState.done = 0;
      this.deleteState.total = targets.length;
      this.deleteState.results = [];

      const CHUNK = 5;
      const url = fc.cloudFunctionUrl + "apiDeleteDuplicateCompanies";

      try {
        for (let i = 0; i < targets.length; i += CHUNK) {
          const chunk = targets.slice(i, i + CHUNK);
          const response = await axios.post(
            url,
            {
              companyIds: chunk.map((t) => t.id),
              expectedSourceCompanyId: sourceCompanyId,
            },
            {
              headers: { "Content-Type": "application/json" },
              timeout: 540000,
              validateStatus: () => true,
            }
          );

          const data = response.data || {};
          if (!data.success) {
            this.deleteState.error =
              (data.error || "Delete failed") +
              (data.code ? " (" + data.code + ")" : "");
            break;
          }

          const chunkResults = (data.data && data.data.results) || [];
          for (const r of chunkResults) {
            this.deleteState.results.push(r);
          }
          this.deleteState.done += chunk.length;
        }
      } catch (err) {
        console.error("runDelete error:", err);
        this.deleteState.error = (err && err.message) || "Network error";
      } finally {
        this.deleteState.running = false;
        this.deleteState.finished = true;
        const okIds = new Set(
          this.deleteState.results.filter((r) => r.ok).map((r) => r.id)
        );
        if (okIds.size > 0) {
          this.selectedDuplicates = this.selectedDuplicates.filter(
            (s) => !okIds.has(s.id)
          );
          await this.loadPreviousCopiesCount();
        }
        const allOk =
          !this.deleteState.error &&
          this.deleteState.results.length === this.deleteState.total &&
          this.deleteState.results.every((r) => r.ok);
        if (allOk) {
          setTimeout(() => {
            if (this.deleteState.finished && !this.deleteState.running) {
              this.deleteDialog.show = false;
            }
          }, 1200);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
